import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import { sendResetPasswordLinkSchema, SendResetPasswordLinkRequest } from '$/requests/SendResetPasswordLink.ts';

const ForgotPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<SendResetPasswordLinkRequest>({
    resolver: zodResolver(sendResetPasswordLinkSchema),
    defaultValues: {
      email: '',
    },
  });
  const {
    sendResetPasswordLinkQuery: {
      mutateAsync: resetPassword,
      isPending: isLoadingResetPassword,
      error: errorResetPassword,
      isError: isErrorResetPassword,
      isSuccess: isSuccessResetPassword,
    },
  } = useAuthContext();

  const isLoading = isLoadingResetPassword;

  const handleSendResetPasswordLink = (data: SendResetPasswordLinkRequest) => {
    void (async () => {
      try {
        if (isLoading) return;

        await resetPassword({
          email: data.email,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <form className="w-full max-w-[428px] flex flex-col gap-4" onSubmit={handleSubmit(handleSendResetPasswordLink)}>
        <div className="flex flex-col gap-4">
          <h1 className="text-xl sm:text-2xl font-semibold">{t('forgotPasswordPage.header')}</h1>
          {isSuccessResetPassword && <Alert status="success">{t('forgotPasswordPage.successInfo')}</Alert>}
          {isErrorResetPassword && <Alert status="error">{errorResetPassword?.message}</Alert>}
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                disabled={isSuccessResetPassword}
                label={t('label.email')}
                placeholder={t('placeholder.email')}
              />
            )}
          />
          <LoadingButton
            className="mt-2 w-full"
            loading={isLoading}
            disabled={isSuccessResetPassword}
            type="submit"
            color="neutral"
          >
            {t('button.sendResetPasswordLink')}
          </LoadingButton>
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default ForgotPasswordPage;
